/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  FormEvent,
  MouseEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState
} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {
  UserMoveEditorFormFields
} from '@ion/core/src/types/editors';
import {
  Account,
  User
} from '@ion/core/src/types/entities';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {isValue} from '@ion/core/src/utils/StringUtil';
import Chip from '@ion/dls/src/components/chip/Chip';
import FormGroupFields from '@ion/dls/src/components/forms/FormGroupFields';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {
  useAnimatedMount
} from '../hooks/useAnimatedMount';
import {
  useExtendStyles
} from '../hooks/useExtendStyles';
import {useFormState} from '../hooks/useFormState';
import {
  useMedia
} from '../hooks/useMedia';
import {
  useMergeState
} from '../hooks/useMergeState';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import {DLSTheme, IconSize} from '../types/types';
import EditorDialog from './EditorDialog';
import styles from './editorStyles';
import GroupChooser from './GroupChooser';
import {deserialize, serialize} from './UserEditorUtils';

const styleOverrides = (theme: DLSTheme) => ({
  chipContainer : {
    display  : 'flex',
    flexWrap : 'wrap'
  },
  chipWrapper : {
    marginBottom : '10px',
    position     : 'relative'
  },
  targetAccount : {
    marginTop : '-50px'
  }
});

const dialogOverrides = (theme: DLSTheme) => ({
  form : {
    height : '100%'
  },
  formPaper : {
    minWidth    : '70% !important',
    paddingLeft : '2px !important',
    height      : '521px',
    minHeight   : 'unset !important'
  },
  paperScrollPaper : {
    [theme.breakpoints.down('sm')] : {
      minHeight : '100%'
    }
  }
});

// defines the User bulk editor prop type definitions ...
interface UserMoveEditorProps {
  open: boolean;
  users?: User[];
  account: Account;
  clear?: boolean;
  loading?: boolean;
  onSave: (users: User[]) => void;
  onCancel?: (event: SyntheticEvent<{}, Event>) => void;
  isCtcAdmin?: boolean;
}

// defines the text input prop type definitions ...
interface TargetType {
  name: UserMoveEditorFormFields,
  value: unknown;
  required?: boolean
}

/**
   * The User Bulk edit Form with set fields and encapsulated logic for handling the form's state
   *
   * @param  open Determines if the UserMoveEditor dialog should be open
   * @param  account User's account object
   * @param  onCancel Callback function fired when the dialog is to be closed.
   * @param  onSave Function the be run when the form is submitted.
   *         The function is passed an user object
   * @param  users Users that passed to the form
   * @param  clear If true we clear the form state
   * @param loading disables any for submission while the form is busy
   */
export const UserMoveEditor = ({
  open,
  account,
  onSave,
  onCancel,
  users = [],
  clear = false,
  loading = false,
  isCtcAdmin = false
}: UserMoveEditorProps) => {
  const editorStyles                          = useExtendStyles(styles, styleOverrides);
  const classes                               = editorStyles();
  const {isPhone}                             = useMedia();
  const {isVisible, onRemove}                 = useAnimatedMount();
  const [formState, setFormState]             = useFormState();
  const [userDatas, setUserDatas]             = useState<User[]>();
  const [groupsLoading, setGroupsLoading]     = useState<boolean>(false);
  const [groupValue, setGroupValue]           = useState(null);
  const [selectedUsers, setSelectedUsers]     = useState([]);
  const [openDatePickers, setOpenDatePickers] = useMergeState();
  const [isFormChanged, setIsFormChanged]     = useState(false);
  const entityLabel                           = translate('common:entityLabels.users');

  useEffect(() => {
    if (!isObjectEmpty(users) && open) {
      const deserializedUsers = users.map(user => deserialize(user, null, null));
      setUserDatas(deserializedUsers);
      setIsFormChanged(false);
      setSelectedUsers(users);
    }
  }, [open]);

  useEffect(() => {
    if (clear) {
      clearForm();
    }
  }, [clear]);

  const clearForm = useCallback(() => {
    setFormState({}, true);
    setIsFormChanged(false);
  }, []);

  const handleChooserChange = useCallback(({name, value, required = false}) => {
    setFormState({[name]: {name, value: isValue(value, 'none') ? null : value, required}});
    setIsFormChanged(true);
  }, []);

  const handleSubmit = useCallback((e: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const userSerializeDatas = userDatas?.map(user => {
      const newUser = {
        ...user,
        account : {
          key   : 'account',
          value : formState?.groups?.value
        }
      };
      return serialize({state: newUser});
    });
    onSave?.(userSerializeDatas);
  }, [userDatas, formState]);

  const onClose = useCallback(() => {
    onRemove(onCancel);
  }, []);

  const removeUser = useCallback((selectedItem) => {
    const filteredTools = selectedUsers.filter((tool) => {
      return tool.id !== selectedItem.id;
    });
    const deserializedUsers = filteredTools.map(user => deserialize(user, null, null));
    setUserDatas(deserializedUsers);
    setSelectedUsers(filteredTools);
  }, [selectedUsers, deserialize]);

  const onChipDelete = useCallback((option) => {
    removeUser(option);
  }, [removeUser]);

  return (
    <EditorDialog open={isVisible}
      dialogTitle={translate('common:editors.move.title', {
        entity : entityLabel
      })}
      onCloseDialog={onClose}
      onCancelDialog={onClose}
      onPrimaryButtonClick={handleSubmit}
      primaryButtonDisabled={!isFormChanged}
      primaryButtonLoading={loading}
      styleOverrides={dialogOverrides} >
      <form className={classes.form} onSubmit={handleSubmit}>
        <div className={classes.formWrapper}>
          <FormSection title={translate('admin-users:form.move.selected')}>
            <FormGroupFields>
              <FormItem fullWidth>
                <div className={classes.chipContainer}>
                  {selectedUsers?.length > 1 && selectedUsers.map((selected, index) => {
                    return (
                      <div key={selected.id} className={classes.chipWrapper}>
                        <Chip label={selected.username}
                          size='small'
                          maxWidth={false}
                          borderRadius='56px'
                          labelColor={dlsColor('grey2')}
                          hoverDefault
                          showCustomDeleteIcon
                          noTextTransform
                          customDeleteIcon={<MaterialExitCloseIcon size={IconSize.small} />}
                          onDelete={function() {
                            onChipDelete(selected);
                          }}/>
                      </div>);
                  })}
                  {selectedUsers?.length === 1 && selectedUsers.map((selected, index) => {
                    return (
                      <div key={selected.id} className={classes.chipWrapper}>
                        <Chip label={selected.username}
                          size='small'
                          maxWidth={false}
                          borderRadius='56px'
                          labelColor={dlsColor('grey2')}
                          hoverDefault
                          showCustomDeleteIcon
                          noTextTransform/>
                      </div>);
                  })}
                </div>
              </FormItem>
            </FormGroupFields>
          </FormSection>
          <div className={classes.targetAccount}>
            <FormSection title={translate('admin-users:form.move.targetAccount')}>
              <FormGroupFields>
                <FormItem fullWidth>
                  <GroupChooser onLoad={setGroupsLoading}
                    includeGroupProperties={false}
                    onChange={function(value) {
                      setGroupValue(value);
                      handleChooserChange({
                        name : UserMoveEditorFormFields.Groups,
                        value
                      });
                    }}
                    hasError={formState[UserMoveEditorFormFields.Groups]?.error}
                    defaultValue={formState[UserMoveEditorFormFields.Groups]?.value} />
                </FormItem>
              </FormGroupFields>
            </FormSection>
          </div>
        </div>
      </form>
    </EditorDialog>
  );
};

export default UserMoveEditor;
